import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { ArrowLeft, Copy, ScrollText, Trash2, Users } from 'lucide-react'
import type { PermissionCode } from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Empty,
  Input,
  PageHeader,
  Select,
  Table,
  Tabs,
  Textarea,
  useAuth,
  useCan,
  usePermissions,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import {
  fullName,
  formatDate,
  SCOPE_LABELS,
  USER_STATUS,
  type AdminUser,
  type PermissionModule,
  type Role,
  type ScopeType,
  type SodRule,
} from '../../lib/rbac-types'
import { CreateRoleModal, RoleBadges, ROLES_KEY, useRoles } from './RolesPage'

const SUPER_ADMIN = 'super_admin'

interface Holder {
  assignmentId: number
  scopeType: ScopeType
  scopeId: string | null
  validUntil: string | null
  user: Pick<AdminUser, 'id' | 'email' | 'firstName' | 'lastName' | 'status'>
}

/** Règles de séparation des responsabilités violées par un ensemble de permissions. */
export function sodViolations(rules: SodRule[], selected: ReadonlySet<string>) {
  return rules.filter((rule) => selected.has(rule.a) && selected.has(rule.b))
}

/** Éditeur de profil : matrice de permissions, informations, titulaires (TODO 4.6). */
export function RoleEditorPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const { session } = useAuth()
  const [tab, setTab] = useState('permissions')

  const role = useQuery({
    queryKey: [...ROLES_KEY, id],
    queryFn: () => session.api.get<Role & { permissions: string[] }>(`/admin/roles/${id}`),
  })

  if (role.isError)
    return (
      <>
        <BackLink />
        <Alert tone="danger">{errorMessage(role.error)}</Alert>
      </>
    )
  if (!role.data) return <BackLink />

  return (
    <>
      <BackLink />
      <PageHeader
        eyebrow={<span className="mono">{role.data.code}</span>}
        title={role.data.label}
        description={role.data.description ?? undefined}
        actions={
          <>
            <RoleBadges role={role.data} />
            <Can perm="audit.view">
              <Link
                className="btn btn-ghost btn-sm"
                to={`/audit?ressource=role&id=${encodeURIComponent(role.data.id)}`}
              >
                <ScrollText aria-hidden="true" /> {t('audit.journal')}
              </Link>
            </Can>
          </>
        }
      />
      <Tabs
        label={t('rbac.roles.title')}
        value={tab}
        onChange={setTab}
        items={[
          { value: 'permissions', label: t('rbac.editor.tabPermissions') },
          { value: 'info', label: t('rbac.editor.tabInfo') },
          { value: 'holders', label: t('rbac.editor.tabHolders') },
        ]}
      />
      <div style={{ marginBlockStart: 'var(--space-5)' }}>
        {tab === 'permissions' && (
          // Remonté quand les permissions enregistrées changent → la sélection repart de l'état serveur.
          <PermissionMatrix key={role.data.permissions.join(',')} role={role.data} />
        )}
        {tab === 'info' && <RoleInfo role={role.data} />}
        {tab === 'holders' && <RoleHolders roleId={role.data.id} />}
      </div>
    </>
  )
}

function BackLink() {
  const { t } = useTranslation()
  return (
    <Link
      to="/profils"
      className="btn btn-ghost btn-sm"
      style={{ marginBlockEnd: 'var(--space-3)' }}
    >
      <ArrowLeft aria-hidden="true" /> {t('rbac.editor.back')}
    </Link>
  )
}

/* ------------------------------------------------------------------ */
/* Matrice de permissions                                              */
/* ------------------------------------------------------------------ */

function PermissionMatrix({ role }: { role: Role & { permissions: string[] } }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canUpdate = useCan('roles.update')
  const isSuperAdmin = role.code === SUPER_ADMIN
  const editable = canUpdate && !isSuperAdmin

  const catalog = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: () => session.api.get<{ modules: PermissionModule[] }>('/admin/permissions'),
    staleTime: 5 * 60_000,
  })
  const sod = useQuery({
    queryKey: ['admin', 'sod-rules'],
    queryFn: () => session.api.get<{ data: SodRule[] }>('/admin/sod-rules').then((r) => r.data),
    staleTime: 5 * 60_000,
  })

  const initial = useMemo(() => new Set(role.permissions), [role.permissions])
  const [selected, setSelected] = useState<Set<string>>(() => new Set(role.permissions))
  const [confirming, setConfirming] = useState(false)

  const added = [...selected].filter((code) => !initial.has(code))
  const removed = [...initial].filter((code) => !selected.has(code))
  const dirty = added.length + removed.length > 0
  const violations = sodViolations(sod.data ?? [], selected)
  const blocking = violations.some((rule) => rule.mode === 'block')

  const labels = useMemo(() => {
    const map = new Map<string, string>()
    for (const module of catalog.data?.modules ?? [])
      for (const p of module.permissions) map.set(p.code, p.label)
    return map
  }, [catalog.data])

  /** Anti-escalade (miroir de l'API) : on ne touche qu'aux permissions qu'on détient. */
  const canToggle = (code: string) => editable && can(code as PermissionCode)

  const toggle = (code: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (on) next.add(code)
      else next.delete(code)
      return next
    })

  const save = useMutation({
    mutationFn: (reason: string) =>
      session.api.put<{ added: string[]; removed: string[]; warnings: { message: string }[] }>(
        `/admin/roles/${role.id}/permissions`,
        { permissions: [...selected], reason }
      ),
    onSuccess: async (result) => {
      setConfirming(false)
      toast.success(t('rbac.editor.saved'), result.warnings.map((w) => w.message).join(' '))
      await queryClient.invalidateQueries({ queryKey: ROLES_KEY })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  if (catalog.isError) return <Alert tone="danger">{errorMessage(catalog.error)}</Alert>
  if (!catalog.data) return null

  return (
    <div className="stack" style={{ gap: 'var(--space-4)' }}>
      {isSuperAdmin && <Alert tone="info">{t('rbac.editor.superAdminNote')}</Alert>}
      {!canUpdate && !isSuperAdmin && <Alert tone="info">{t('rbac.editor.readOnly')}</Alert>}

      {violations.map((rule) => (
        <Alert
          key={rule.code}
          tone={rule.mode === 'block' ? 'danger' : 'warning'}
          title={rule.mode === 'block' ? t('rbac.editor.sodBlock') : t('rbac.editor.sodWarn')}
        >
          {rule.message} ({labels.get(rule.a) ?? rule.a} + {labels.get(rule.b) ?? rule.b})
        </Alert>
      ))}

      {editable && (
        <Card className="perm-savebar">
          <CardBody>
            <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <span aria-live="polite">
                {dirty
                  ? t('rbac.editor.changes', { added: added.length, removed: removed.length })
                  : t('rbac.editor.noChange')}
              </span>
              <span className="row" style={{ gap: 'var(--space-2)' }}>
                <Button
                  variant="secondary"
                  disabled={!dirty}
                  onClick={() => setSelected(new Set(initial))}
                >
                  {t('rbac.editor.reset')}
                </Button>
                <Button
                  variant="primary"
                  disabled={!dirty || blocking}
                  onClick={() => setConfirming(true)}
                >
                  {t('rbac.editor.save')}
                </Button>
              </span>
            </div>
          </CardBody>
        </Card>
      )}

      <div>
        {catalog.data.modules.map((module) => {
          const toggleable = module.permissions.filter((p) => canToggle(p.code))
          const allOn = toggleable.length > 0 && toggleable.every((p) => selected.has(p.code))
          return (
            <section key={module.code} className="perm-module" aria-label={module.label}>
              <div className="perm-module-hd">
                <span>{module.label}</span>
                {toggleable.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setSelected((prev) => {
                        const next = new Set(prev)
                        for (const p of toggleable) {
                          if (allOn) next.delete(p.code)
                          else next.add(p.code)
                        }
                        return next
                      })
                    }
                  >
                    {allOn ? t('rbac.editor.none') : t('rbac.editor.all')}
                  </Button>
                )}
              </div>
              <div className="perm-grid">
                {module.permissions.map((permission) => {
                  const checked = isSuperAdmin || selected.has(permission.code)
                  const changed = added.includes(permission.code)
                    ? 'added'
                    : removed.includes(permission.code)
                      ? 'removed'
                      : undefined
                  const notHeld = editable && !can(permission.code as PermissionCode)
                  return (
                    <label
                      key={permission.code}
                      className="check-row perm-item"
                      data-changed={changed}
                      title={notHeld ? t('rbac.editor.notHeld') : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!canToggle(permission.code)}
                        onChange={(e) => toggle(permission.code, e.target.checked)}
                      />
                      <span>
                        <span>{permission.label}</span>{' '}
                        {permission.sensitive && (
                          <Badge variant="danger">{t('rbac.editor.sensitive')}</Badge>
                        )}
                        <span className="perm-code">{permission.code}</span>
                      </span>
                    </label>
                  )
                })}
              </div>
            </section>
          )
        })}
      </div>

      <ConfirmModal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={t('rbac.editor.confirmTitle')}
        message={
          <div className="stack" style={{ gap: 'var(--space-3)' }}>
            <p>{t('rbac.editor.confirmText')}</p>
            <DiffList title={t('rbac.editor.added')} codes={added} labels={labels} tone="success" />
            <DiffList
              title={t('rbac.editor.removed')}
              codes={removed}
              labels={labels}
              tone="danger"
            />
            {violations.length > 0 && (
              <Alert tone="warning">{violations.map((v) => v.message).join(' ')}</Alert>
            )}
          </div>
        }
        reasonRequired
        confirmLabel={t('rbac.editor.save')}
        onConfirm={async (reason) => {
          await save.mutateAsync(reason ?? '').catch(() => undefined)
        }}
      />
    </div>
  )
}

function DiffList({
  title,
  codes,
  labels,
  tone,
}: {
  title: string
  codes: string[]
  labels: Map<string, string>
  tone: 'success' | 'danger'
}) {
  if (!codes.length) return null
  return (
    <div>
      <strong>
        {title} ({codes.length})
      </strong>
      <ul style={{ margin: 0, paddingInlineStart: 'var(--space-5)' }}>
        {codes.map((code) => (
          <li key={code}>
            <Badge variant={tone}>{tone === 'success' ? '+' : '−'}</Badge>{' '}
            {labels.get(code) ?? code}{' '}
            <span className="perm-code" style={{ display: 'inline' }}>
              {code}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Informations                                                        */
/* ------------------------------------------------------------------ */

function RoleInfo({ role }: { role: Role }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const roles = useRoles()
  const canUpdate = useCan('roles.update') && role.code !== SUPER_ADMIN
  const [form, setForm] = useState({
    label: role.label,
    description: role.description ?? '',
    requiresMfa: role.requiresMfa,
    defaultScope: role.defaultScope,
    isActive: role.isActive,
  })
  const [duplicating, setDuplicating] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const update = useMutation({
    mutationFn: () =>
      session.api.patch<Role>(`/admin/roles/${role.id}`, {
        ...form,
        description: form.description || null,
      }),
    onSuccess: async () => {
      toast.success(t('rbac.user.done'))
      await queryClient.invalidateQueries({ queryKey: ROLES_KEY })
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const remove = useMutation({
    mutationFn: () => session.api.delete(`/admin/roles/${role.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ROLES_KEY })
      navigate('/profils', { replace: true })
    },
    onError: (error) => {
      setDeleting(false)
      toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(update.error)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    update.mutate()
  }

  return (
    <Card>
      <CardHeader
        title={t('rbac.editor.tabInfo')}
        actions={
          <span className="row" style={{ gap: 'var(--space-2)' }}>
            <Can perm="roles.create">
              {role.code !== SUPER_ADMIN && (
                <Button variant="secondary" icon={<Copy />} onClick={() => setDuplicating(true)}>
                  {t('rbac.editor.duplicate')}
                </Button>
              )}
            </Can>
            <Can perm="roles.delete">
              {!role.isSystem && (
                <Button variant="danger" icon={<Trash2 />} onClick={() => setDeleting(true)}>
                  {t('rbac.editor.delete')}
                </Button>
              )}
            </Can>
          </span>
        }
      />
      <CardBody>
        <form className="stack" onSubmit={submit}>
          <fieldset disabled={!canUpdate} className="stack" style={{ border: 0, padding: 0 }}>
            <Input
              label={t('rbac.create.label')}
              required
              value={form.label}
              error={errors.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
            <Textarea
              label={t('rbac.create.descriptionField')}
              rows={3}
              value={form.description}
              error={errors.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <Select
              label={t('rbac.editor.defaultScope')}
              value={form.defaultScope}
              onChange={(e) => setForm({ ...form, defaultScope: e.target.value as ScopeType })}
              options={(Object.keys(SCOPE_LABELS) as ScopeType[]).map((value) => ({
                value,
                label: SCOPE_LABELS[value],
              }))}
            />
            <label className="check-row">
              <input
                type="checkbox"
                checked={form.requiresMfa}
                onChange={(e) => setForm({ ...form, requiresMfa: e.target.checked })}
              />
              <span>
                {t('rbac.editor.requiresMfa')}
                <span className="field-hint" style={{ display: 'block' }}>
                  {t('rbac.editor.requiresMfaHint')}
                </span>
              </span>
            </label>
            <label className="check-row">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              <span>
                {t('rbac.editor.active')}
                <span className="field-hint" style={{ display: 'block' }}>
                  {t('rbac.editor.activeHint')}
                </span>
              </span>
            </label>
          </fieldset>
          {canUpdate && (
            <div>
              <Button variant="primary" type="submit" loading={update.isPending}>
                {t('rbac.editor.infoSave')}
              </Button>
            </div>
          )}
        </form>
      </CardBody>
      <CreateRoleModal
        open={duplicating}
        onClose={() => setDuplicating(false)}
        roles={roles.data ?? []}
        sourceId={role.id}
      />
      <ConfirmModal
        open={deleting}
        onClose={() => setDeleting(false)}
        title={t('rbac.editor.deleteTitle')}
        message={t('rbac.editor.deleteText')}
        tone="danger"
        confirmLabel={t('rbac.editor.delete')}
        onConfirm={async () => {
          await remove.mutateAsync().catch(() => undefined)
        }}
      />
    </Card>
  )
}

/* ------------------------------------------------------------------ */
/* Titulaires                                                          */
/* ------------------------------------------------------------------ */

function RoleHolders({ roleId }: { roleId: string }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const navigate = useNavigate()
  const canViewUsers = useCan('users.view')
  const holders = useQuery({
    queryKey: [...ROLES_KEY, roleId, 'users'],
    queryFn: () =>
      session.api.get<{ data: Holder[] }>(`/admin/roles/${roleId}/users`).then((r) => r.data),
    enabled: canViewUsers,
  })

  if (!canViewUsers) return <Alert tone="info">{t('errors.forbiddenText')}</Alert>

  const columns: Column<Holder>[] = [
    { key: 'name', header: t('rbac.users.name'), cell: (h) => fullName(h.user) },
    { key: 'email', header: t('rbac.users.email'), cell: (h) => h.user.email },
    { key: 'scope', header: t('rbac.editor.scope'), cell: (h) => SCOPE_LABELS[h.scopeType] },
    { key: 'until', header: t('rbac.editor.until'), cell: (h) => formatDate(h.validUntil) },
    {
      key: 'status',
      header: t('rbac.users.status'),
      cell: (h) => (
        <Badge variant={USER_STATUS[h.user.status].variant}>
          {USER_STATUS[h.user.status].label}
        </Badge>
      ),
    },
  ]

  return (
    <Card>
      <Table
        columns={columns}
        rows={holders.data ?? []}
        rowKey={(h) => String(h.assignmentId)}
        loading={holders.isPending}
        caption={t('rbac.editor.tabHolders')}
        onRowClick={(h) => navigate(`/utilisateurs/${h.user.id}`)}
        empty={<Empty icon={<Users aria-hidden="true" />} title={t('rbac.editor.holdersEmpty')} />}
      />
    </Card>
  )
}
